import { type FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { PopoverCombobox } from '@/components/ui/popover-combobox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ALL_COUNTRIES } from '@/shared/constants/countries'
import {
  ADD_ASSOCIATED_PARTY_VALIDATION_CODES,
  addAssociatedPartyFormSchema,
  type AddAssociatedPartyFormValues,
} from './AddAssociatedPartyForm.schema'

type AssociatedPartyFormValue = string | null | undefined

export type AssociatedPartyFormInitialValues = {
  name?: AssociatedPartyFormValue
  street?: AssociatedPartyFormValue
  street2?: AssociatedPartyFormValue
  streetNumber?: AssociatedPartyFormValue
  subDivision?: AssociatedPartyFormValue
  country?: AssociatedPartyFormValue
  postCode?: AssociatedPartyFormValue
  poBox?: AssociatedPartyFormValue
  city?: AssociatedPartyFormValue
  contactName?: AssociatedPartyFormValue
  contactPhoneNumber?: AssociatedPartyFormValue
  contactEmail?: AssociatedPartyFormValue
}

type AssociatedPartyFormState = Record<keyof AssociatedPartyFormInitialValues, string>

const EMPTY_FORM_VALUES: AssociatedPartyFormState = {
  name: '',
  street: '',
  street2: '',
  streetNumber: '',
  subDivision: '',
  country: '',
  postCode: '',
  poBox: '',
  city: '',
  contactName: '',
  contactPhoneNumber: '',
  contactEmail: '',
}

const normalizeValue = (value: AssociatedPartyFormValue) => value ?? ''

function buildFormState(
  initialValues?: AssociatedPartyFormInitialValues
): AssociatedPartyFormState {
  return {
    name: normalizeValue(initialValues?.name),
    street: normalizeValue(initialValues?.street),
    street2: normalizeValue(initialValues?.street2),
    streetNumber: normalizeValue(initialValues?.streetNumber),
    subDivision: normalizeValue(initialValues?.subDivision),
    country: normalizeValue(initialValues?.country),
    postCode: normalizeValue(initialValues?.postCode),
    poBox: normalizeValue(initialValues?.poBox),
    city: normalizeValue(initialValues?.city),
    contactName: normalizeValue(initialValues?.contactName),
    contactPhoneNumber: normalizeValue(initialValues?.contactPhoneNumber),
    contactEmail: normalizeValue(initialValues?.contactEmail),
  }
}

type AssociatedPartyFormProps = {
  open: boolean
  initialValues?: AssociatedPartyFormInitialValues
  onSubmit: (payload: AddAssociatedPartyFormValues) => void
  onCancel: () => void
  submitLabel: string
  cancelLabel: string
  isPending: boolean
  inputIdPrefix: string
}

export function AssociatedPartyForm({
  open,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  isPending,
  inputIdPrefix,
}: AssociatedPartyFormProps) {
  const { t } = useTranslation(['associatedParties', 'countries'])
  const [values, setValues] = useState<AssociatedPartyFormState>(
    EMPTY_FORM_VALUES
  )

  useEffect(() => {
    if (open) {
      setValues(buildFormState(initialValues))
    }
  }, [open, initialValues])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const result = addAssociatedPartyFormSchema.safeParse(values)

    if (!result.success) {
      const firstIssue = result.error.issues[0]
      const code = typeof firstIssue.message === 'string' ? firstIssue.message : ''
      const i18nKey =
        ADD_ASSOCIATED_PARTY_VALIDATION_CODES[
          code as keyof typeof ADD_ASSOCIATED_PARTY_VALIDATION_CODES
        ]
      const message = i18nKey ? t(i18nKey) : code || t('addModal.validationName')
      toast.error(message)
      return
    }

    onSubmit(result.data)
  }

  return (
    <form onSubmit={handleSubmit} className='flex min-h-0 flex-1 flex-col gap-4'>
      <div className='min-h-0 flex-1 space-y-4 overflow-y-auto pr-1'>
        <div className='space-y-2'>
          <Label htmlFor={`${inputIdPrefix}-name`}>
            {t('addModal.name')} <span aria-hidden>*</span>
          </Label>
          <Input
            id={`${inputIdPrefix}-name`}
            value={values.name}
            onChange={(event) =>
              setValues((currentValues) => ({
                ...currentValues,
                name: event.target.value,
              }))
            }
            placeholder={t('addModal.namePlaceholder')}
            disabled={isPending}
          />
        </div>

        <fieldset className='space-y-3 rounded-md border p-3'>
          <legend className='px-1 text-sm font-medium'>
            {t('addModal.addressSection')}
          </legend>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <div className='space-y-2 sm:col-span-2'>
              <Label htmlFor={`${inputIdPrefix}-street`}>{t('addModal.street')}</Label>
              <Input
                id={`${inputIdPrefix}-street`}
                value={values.street}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    street: event.target.value,
                  }))
                }
                placeholder={t('addModal.streetPlaceholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-streetNumber`}>
                {t('addModal.streetNumber')}
              </Label>
              <Input
                id={`${inputIdPrefix}-streetNumber`}
                value={values.streetNumber}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    streetNumber: event.target.value,
                  }))
                }
                placeholder={t('addModal.streetNumberPlaceholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-street2`}>{t('addModal.street2')}</Label>
              <Input
                id={`${inputIdPrefix}-street2`}
                value={values.street2}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    street2: event.target.value,
                  }))
                }
                placeholder={t('addModal.street2Placeholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-postCode`}>
                {t('addModal.postCode')}
              </Label>
              <Input
                id={`${inputIdPrefix}-postCode`}
                value={values.postCode}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    postCode: event.target.value,
                  }))
                }
                placeholder={t('addModal.postCodePlaceholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-city`}>
                {t('addModal.city')} <span aria-hidden>*</span>
              </Label>
              <Input
                id={`${inputIdPrefix}-city`}
                value={values.city}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    city: event.target.value,
                  }))
                }
                placeholder={t('addModal.cityPlaceholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2 sm:col-span-2'>
              <Label htmlFor={`${inputIdPrefix}-subDivision`}>
                {t('addModal.subDivision')}
              </Label>
              <Input
                id={`${inputIdPrefix}-subDivision`}
                value={values.subDivision}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    subDivision: event.target.value,
                  }))
                }
                placeholder={t('addModal.subDivisionPlaceholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-country`}>
                {t('addModal.country')} <span aria-hidden>*</span>
              </Label>
              <PopoverCombobox
                id={`${inputIdPrefix}-country`}
                items={ALL_COUNTRIES}
                value={values.country || null}
                onValueChange={(code) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    country: code ?? '',
                  }))
                }
                getItemLabel={(code) => t(code, { ns: 'countries' }) || code}
                getItemKey={(code) => code}
                placeholder={t('addModal.countryPlaceholder')}
                searchPlaceholder={t('addModal.countrySearchPlaceholder')}
                emptyText={t('addModal.countryEmpty')}
                disabled={isPending}
                showClear
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-poBox`}>{t('addModal.poBox')}</Label>
              <Input
                id={`${inputIdPrefix}-poBox`}
                value={values.poBox}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    poBox: event.target.value,
                  }))
                }
                placeholder={t('addModal.poBoxPlaceholder')}
                disabled={isPending}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className='space-y-3 rounded-md border p-3'>
          <legend className='px-1 text-sm font-medium'>
            {t('addModal.contactSection')}
          </legend>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-contactName`}>
                {t('addModal.contactName')}
              </Label>
              <Input
                id={`${inputIdPrefix}-contactName`}
                value={values.contactName}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    contactName: event.target.value,
                  }))
                }
                placeholder={t('addModal.contactNamePlaceholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-contactPhoneNumber`}>
                {t('addModal.contactPhoneNumber')}
              </Label>
              <Input
                id={`${inputIdPrefix}-contactPhoneNumber`}
                type='tel'
                value={values.contactPhoneNumber}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    contactPhoneNumber: event.target.value,
                  }))
                }
                placeholder={t('addModal.contactPhoneNumberPlaceholder')}
                disabled={isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${inputIdPrefix}-contactEmail`}>
                {t('addModal.contactEmail')}
              </Label>
              <Input
                id={`${inputIdPrefix}-contactEmail`}
                type='email'
                value={values.contactEmail}
                onChange={(event) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    contactEmail: event.target.value,
                  }))
                }
                placeholder={t('addModal.contactEmailPlaceholder')}
                disabled={isPending}
              />
            </div>
          </div>
        </fieldset>
      </div>
      <div className='flex shrink-0 justify-end gap-2'>
        <Button type='button' variant='outline' onClick={onCancel} disabled={isPending}>
          {cancelLabel}
        </Button>
        <Button type='submit' disabled={isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
